import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Video, AlertCircle, Youtube, Play, X } from 'lucide-react';
import { parseVideoUrl, generateEmbedHtml, getPlatformLabel, VideoInfo } from '@/lib/video-utils';

interface MarkdownVideoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (embedHtml: string) => void;
}

export function MarkdownVideoDialog({
  open,
  onOpenChange,
  onInsert,
}: MarkdownVideoDialogProps) {
  const [urlInput, setUrlInput] = useState('');
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setUrlInput('');
      setVideoInfo(null);
      setTitle('');
      setError(null);
    }
  }, [open]);

  // Auto-detect video when URL changes
  useEffect(() => {
    if (!urlInput.trim()) {
      setVideoInfo(null);
      setError(null);
      return;
    }

    const info = parseVideoUrl(urlInput);
    if (info.platform === 'unknown') {
      setVideoInfo(null);
      setError('Unsupported video URL. Please use YouTube, Vimeo, or Loom links.');
    } else {
      setVideoInfo(info);
      setError(null);
    }
  }, [urlInput]);

  const handleInsert = useCallback(() => {
    if (!videoInfo) return;

    const embedHtml = generateEmbedHtml(videoInfo, title.trim() || undefined);
    onInsert(embedHtml);
    onOpenChange(false);
  }, [videoInfo, title, onInsert, onOpenChange]);

  const clearVideo = useCallback(() => {
    setUrlInput('');
    setVideoInfo(null);
    setTitle('');
    setError(null);
  }, []);

  const getPlatformIcon = (platform: VideoInfo['platform']) => {
    switch (platform) {
      case 'youtube':
        return <Youtube className="h-4 w-4 text-red-500" />;
      case 'vimeo':
        return <Play className="h-4 w-4 text-blue-500" />;
      case 'loom':
        return <Video className="h-4 w-4 text-purple-500" />;
      default:
        return <Video className="h-4 w-4" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Embed Video
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="video-url">Video URL</Label>
            <Input
              id="video-url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="Paste YouTube, Vimeo, or Loom URL..."
            />
            {error && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Supported: YouTube, Vimeo, Loom
            </p>
          </div>

          {/* Video Preview */}
          {videoInfo && (
            <div className="space-y-4">
              <div className="relative rounded-lg border bg-muted/50 overflow-hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 z-10 h-6 w-6 bg-background/80 hover:bg-background"
                  onClick={clearVideo}
                >
                  <X className="h-4 w-4" />
                </Button>

                {/* Platform Badge */}
                <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 bg-background/80 rounded text-xs font-medium">
                  {getPlatformIcon(videoInfo.platform)}
                  {getPlatformLabel(videoInfo.platform)}
                </div>

                {/* Thumbnail */}
                <div className="aspect-video bg-muted flex items-center justify-center">
                  {videoInfo.thumbnailUrl ? (
                    <img
                      src={videoInfo.thumbnailUrl}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      {getPlatformIcon(videoInfo.platform)}
                      <span className="text-sm">
                        {getPlatformLabel(videoInfo.platform)} Video
                      </span>
                    </div>
                  )}

                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-16 w-16 rounded-full bg-background/80 flex items-center justify-center">
                      <Play className="h-8 w-8 text-foreground ml-1" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Title Input */}
              <div className="space-y-2">
                <Label htmlFor="video-title">Title (optional, for accessibility)</Label>
                <Input
                  id="video-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Describe the video content..."
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={!videoInfo}>
            Embed Video
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
